import { SIZES, SHIELD_PATH, VIEW, shieldMm, eur } from "../lib/shield";

const GAP = 90, FLOOR = 1260;
const CHILD = "M120 210 Q200 190 280 210 L330 230 Q360 240 365 280 L380 600 Q382 630 355 632 Q332 632 330 605 L315 330 L300 330 L305 680 L300 1180 Q298 1200 270 1200 L225 1200 Q210 1200 210 1180 L205 760 L195 760 L190 1180 Q190 1200 175 1200 L130 1200 Q102 1200 100 1180 L95 680 L100 330 L85 330 L70 605 Q68 632 45 632 Q18 630 20 600 L35 280 Q40 240 70 230 Z";
const LIKE = { small: "about an A4 sheet", medium: "about an A3 poster", large: "about an A2 poster", xl: "about an A1 poster" };

const layout = () => {
  let x = 0;
  const place = (w) => { const at = x; x += w + GAP; return at; };
  const child = place(400);
  const shields = SIZES.map((s) => ({ ...s, m: shieldMm(s), x: place(shieldMm(s).w) }));
  const ball = place(220), a4 = place(210);
  return { child, shields, ball, a4, width: x - GAP };
};

const Label = ({ x, y, children, sub }) => (
  <text x={x} y={y} textAnchor="middle" className="font-display" fontSize="44" fontWeight="800" fill="#0f172a">
    {children}{sub && <tspan x={x} dy="46" fontSize="34" fontWeight="600" fill="#475569">{sub}</tspan>}
  </text>
);

// Everything drawn in millimetres so all items stay to scale.
export const SizeChart = ({ active }) => {
  const L = layout();
  return (
    <div className="min-w-0 space-y-4" data-testid="size-chart">
      <div className="overflow-x-auto border-2 border-slate-200 bg-white p-3">
        <svg viewBox={`-20 -40 ${L.width + 160} ${FLOOR + 170}`} className="h-auto w-full min-w-[640px]" role="img" aria-label="Shield sizes drawn to scale next to a 1.2 m child, a football and an A4 sheet">
          {[300, 600, 900, 1200].map((h) => (
            <g key={h}><line x1={0} x2={L.width} y1={FLOOR - h} y2={FLOOR - h} stroke="#e2e8f0" strokeWidth="3" strokeDasharray="14 12" />
              <text x={L.width + 130} y={FLOOR - h + 10} textAnchor="end" fontSize="30" fill="#94a3b8">{`${h / 10} cm`}</text></g>
          ))}
          <g transform={`translate(${L.child} ${FLOOR - 1200})`} data-testid="size-chart-child">
            <circle cx="200" cy="100" r="90" fill="#cbd5e1" /><path d={CHILD} fill="#cbd5e1" />
          </g>
          <Label x={L.child + 200} y={FLOOR + 60} sub="1.2 m · 4 ft">Child</Label>
          {L.shields.map((s) => {
            const on = active === s.id;
            return (
              <g key={s.id} data-testid={`size-chart-shield-${s.id}`}>
                <path d={SHIELD_PATH} transform={`translate(${s.x} ${FLOOR - s.m.h}) scale(${s.m.h / VIEW.h})`}
                  fill={on ? "#a3e635" : "#0f172a"} stroke="#0f172a" strokeWidth={6 * (VIEW.h / s.m.h)} />
                <text x={s.x + s.m.w / 2} y={FLOOR - s.m.h / 2} textAnchor="middle" fontSize="40" fontWeight="800" fill={on ? "#0f172a" : "#ffffff"}>{`${Math.round(s.m.h / 10)} cm`}</text>
                <Label x={s.x + s.m.w / 2} y={FLOOR + 60} sub={eur(s.price)}>{s.name === "Extra Large" ? "XL" : s.name}</Label>
              </g>
            );
          })}
          <g data-testid="size-chart-football">
            <circle cx={L.ball + 110} cy={FLOOR - 110} r="110" fill="#fff" stroke="#0f172a" strokeWidth="6" />
            <polygon points={[0, 1, 2, 3, 4].map((i) => { const a = -Math.PI / 2 + (i * 2 * Math.PI) / 5; return `${L.ball + 110 + 42 * Math.cos(a)},${FLOOR - 110 + 42 * Math.sin(a)}`; }).join(" ")} fill="#0f172a" />
          </g>
          <Label x={L.ball + 110} y={FLOOR + 60} sub="22 cm">Football</Label>
          <rect x={L.a4} y={FLOOR - 297} width="210" height="297" fill="#f8fafc" stroke="#0f172a" strokeWidth="6" data-testid="size-chart-a4" />
          <Label x={L.a4 + 105} y={FLOOR + 60} sub="21 × 30 cm">A4 sheet</Label>
          <line x1={-20} x2={L.width + 20} y1={FLOOR} y2={FLOOR} stroke="#0f172a" strokeWidth="6" />
        </svg>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[520px] text-left text-sm" data-testid="size-chart-table">
          <thead><tr className="border-b-2 border-slate-900 text-xs uppercase tracking-wider text-slate-500">
            <th className="py-2 pr-3">Size</th><th className="py-2 pr-3">Shield size (mm)</th><th className="py-2 pr-3">Roughly</th><th className="py-2 text-right">Price</th>
          </tr></thead>
          <tbody>
            {L.shields.map((s) => (
              <tr key={s.id} className={`border-b border-slate-200 ${active === s.id ? "bg-lime-50 font-bold" : ""}`} data-testid={`size-chart-row-${s.id}`}>
                <td className="py-2 pr-3 font-bold">{s.name}{s.recommended && <span className="ml-1.5 bg-lime-400 px-1 text-[10px] font-black uppercase">Recommended</span>}</td>
                <td className="py-2 pr-3">{Math.round(s.m.w)} × {s.h}</td>
                <td className="py-2 pr-3 text-slate-600">{LIKE[s.id]}</td>
                <td className="py-2 text-right font-cond text-lg font-bold">{eur(s.price)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-slate-500">Drawn to scale. The shield fills the full board height; the board is the printed sheet it's cut from.</p>
    </div>
  );
};
